// src/modules/requerimiento/views/mantenimiento/RevisionSistemas.jsx
// Revisión en Sistemas de un mantenimiento, con el mismo diseño que la app y que la revisión de
// adición: resumen con contadores, equipos agrupados por vehículo (los que quedan, como lista de
// verificación con foto y "¿reporta?"; los reemplazados / devueltos, atenuados) y, al final, la
// nota en PDF y los datos del requerimiento plegados.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, Dialog, DialogActions,
  DialogContent, IconButton, LinearProgress, ListItemIcon, Menu, MenuItem, Stack, Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useParams } from "react-router-dom";
import PrintIcon from "@mui/icons-material/Print";
import MemoryIcon from "@mui/icons-material/Memory";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BuildIcon from "@mui/icons-material/Build";
import CloseIcon from "@mui/icons-material/Close";

import { getRequerimiento } from "../../services/reqSolicitudes";
import { getMantenimientoAsignados, getMantenimientoNotaPdf } from "../../services/mantenimiento";
import ModemImageManager from "../../components/ModemImageManager";
import ENV from "../../../../config/env";
import CentroEquipoDetalleDialog from "../../components/CentroEquipoDetalleDialog";
import ResumenRevision, { COLORES_REVISION } from "../../components/ResumenRevision";
import DatosRequerimientoPlegables from "../../components/DatosRequerimientoPlegables";
import { ItemVerificacion, LineaReporte, PlataformasDialog } from "../../components/ReporteComponents";
import useVerificacionReporte, { claveReporte, contarReportes } from "../../hooks/useVerificacionReporte";
import { Seccion, Grilla, Dato, Observacion } from "../../components/adicion/EquipoInstaladoDialog";

const AZUL_EQUIPOS = "#0284C7";
const SIN_PLACA = "Sin placa";

const extractArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

// El chip puede ya no estar físicamente vinculado al módem (equipo_chip.relacion se limpia
// al reemplazar/devolver), por eso se usa chips_historial como respaldo.
const chipsDe = (row) => {
  if (row?.numero_chip) return [row.numero_chip];
  const hist = row?.chips_historial || [];
  return [...new Set(hist.map((h) => h?.numero).filter(Boolean))];
};

// Las observaciones del mantenimiento llegan unidas con "&".
const observacionDe = (row) =>
  String(row?.observacion ?? "").split("&").map((s) => s.trim()).filter(Boolean).join("\n");

const tieneFoto = (m) => !!String(m?.imagen_url ?? "").trim();

const esActivo = (r) => {
  const ep = (r?.estado_pendiente ?? "").trim();
  const es = (r?.estado ?? "").trim();
  return ep === "Remplazo Adicion" || es === "Activo" || es === "Observado" || (es === "Espera" && ep === "");
};
const motivoRetiro = (r) => {
  if (r?.estado_pendiente === "Remplazo Devuelto") return "Reemplazado";
  if (r?.estado_pendiente === "Devolucion") return "Devuelto";
  return null;
};

export default function RevisionSistemas({ cod: codProp, onToast }) {
  const params = useParams();
  const cod    = useMemo(() => codProp ?? params?.cod, [codProp, params?.cod]);

  const [req,          setReq]          = useState(null);
  const [items,        setItems]        = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [errItems,     setErrItems]     = useState(null);
  const [printing,     setPrinting]     = useState(false);
  const [detalle,      setDetalle]      = useState(null);
  const [plataformasDe, setPlataformasDe] = useState(null);
  const [centroEquipo, setCentroEquipo] = useState(null);
  const { reportes, verificarUno, verificarTodos } = useVerificacionReporte();

  const openCentroEquipo = useCallback((identificador, tipo = "modem") => {
    const value = identificador == null ? "" : String(identificador).trim();
    if (!value) return;
    setCentroEquipo({ tipo, identificador: value });
  }, []);

  const toAbsUrl = useCallback((u) => {
    if (!u) return "";
    if (/^https?:\/\//i.test(u)) return u;
    return ENV.API_BASE_URL ? `${ENV.API_BASE_URL}${u}` : u;
  }, []);

  const fetchReq = useCallback(async () => {
    if (!cod) return;
    try {
      const { data } = await getRequerimiento(cod);
      setReq(data);
    } catch { /* los datos del requerimiento son de consulta: si fallan no se muestran */ }
  }, [cod]);

  const fetchAsignados = useCallback(async () => {
    if (!cod) return [];
    setLoadingItems(true); setErrItems(null);
    try {
      const { data } = await getMantenimientoAsignados(cod);
      const lista = extractArray(data);
      setItems(lista);
      return lista;
    } catch (e) {
      setErrItems(e?.response?.data?.message || e?.message || "Error al cargar equipos asignados");
      return [];
    } finally { setLoadingItems(false); }
  }, [cod]);

  // Solo los equipos que quedan en el vehículo tienen que reportar.
  useEffect(() => {
    fetchReq();
    fetchAsignados().then((lista) => {
      const quedan = lista.filter(esActivo);
      if (quedan.length) verificarTodos(quedan);
    });
  }, [fetchReq, fetchAsignados, verificarTodos]);

  // Al recargar (p. ej. tras subir una foto), el detalle abierto se actualiza.
  useEffect(() => {
    if (!detalle) return;
    const actual = items.find((m) => m.num_sim === detalle.num_sim);
    if (actual && actual !== detalle) setDetalle(actual);
  }, [items, detalle]);

  const handleImprimir = async () => {
    try {
      setPrinting(true);
      const { data } = await getMantenimientoNotaPdf(cod);
      const blob = new Blob([data], { type: "application/pdf" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.target = "_blank"; a.download = `requerimiento-${cod}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      onToast?.({ sev: "error", msg: e?.response?.data?.error || e?.message || "No se pudo generar el PDF" });
    } finally { setPrinting(false); }
  };

  const activos = useMemo(() => items.filter(esActivo), [items]);
  const retirados = useMemo(() => items.filter((r) => !esActivo(r) && motivoRetiro(r)), [items]);

  // Árbol por vehículo: primero los que quedan y después los retirados.
  const grupos = useMemo(() => {
    const mapa = new Map();
    [...activos, ...retirados].forEach((m) => {
      const placa = String(m?.placa ?? "").trim() || SIN_PLACA;
      if (!mapa.has(placa)) mapa.set(placa, []);
      mapa.get(placa).push(m);
    });
    return Array.from(mapa.entries());
  }, [activos, retirados]);

  const total = activos.length;
  const conFoto = activos.filter(tieneFoto).length;
  const { verificando, reportando, desactualizados, sinReporte } = contarReportes(activos, reportes);
  const todoBien = total > 0 && !verificando && reportando === total;
  const mensaje = loadingItems
    ? "Cargando equipos…"
    : total === 0
      ? retirados.length ? "No quedan equipos activos: todos se reemplazaron o devolvieron." : "No hay equipos en este mantenimiento."
      : verificando
        ? "Verificando si los equipos reportan…"
        : todoBien
          ? conFoto < total
            ? `Todo en orden. ${total - conFoto} equipo(s) sin foto del trabajo.`
            : "Todo en orden. Ya se puede finalizar."
          : sinReporte > 0
            ? `${sinReporte} equipo(s) no reportan todavía.`
            : desactualizados > 0
              ? `${desactualizados} equipo(s) no mandan datos hace días.`
              : "Revisa los equipos marcados abajo.";

  return (
    <Stack spacing={2} sx={{ p: { xs: 1.5, sm: 2 } }}>
      <ResumenRevision
        titulo="Revisión del mantenimiento"
        colores={COLORES_REVISION.mantenimiento}
        descripcion="Revisa que los equipos que quedaron en el vehículo estén reportando en la plataforma."
        contadores={total > 0 && !loadingItems ? [
          { etiqueta: "Reportando", valor: verificando ? null : reportando, total },
          { etiqueta: "Con foto", valor: conFoto, total },
          ...(retirados.length ? [{ etiqueta: "Retirados", valor: retirados.length }] : []),
        ] : []}
        mensaje={mensaje}
        todoBien={todoBien}
        onVerificar={!loadingItems && !verificando && total > 0 ? () => verificarTodos(activos) : undefined}
      />

      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Vehículos</Typography>

      {loadingItems ? (
        <LinearProgress />
      ) : errItems ? (
        <Alert severity="error" action={<Button size="small" onClick={fetchAsignados}>Reintentar</Button>}>{errItems}</Alert>
      ) : grupos.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No hay equipos en este mantenimiento.</Typography>
      ) : (
        grupos.map(([placa, modems]) => (
          <Box key={placa}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <DirectionsCarIcon fontSize="small" sx={{ color: "text.secondary" }} />
              <Typography sx={{ fontWeight: 800 }}>{placa}</Typography>
              <Typography variant="body2" color="text.secondary">
                · {modems.length === 1 ? "1 equipo" : `${modems.length} equipos`}
              </Typography>
            </Stack>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5, alignItems: "start" }}>
              {modems.map((m) => (motivoRetiro(m) && !esActivo(m) ? (
                <ModemRetiradoCard key={m.num_sim} modem={m} motivo={motivoRetiro(m)} onVerEquipo={() => openCentroEquipo(m.num_sim)} />
              ) : (
                <ModemActivoCard
                  key={m.num_sim}
                  modem={m}
                  reporte={reportes[claveReporte(m)]}
                  onVer={() => setDetalle(m)}
                  onVerEquipo={() => openCentroEquipo(m.num_sim || m.emai)}
                  onVerReporte={() => setPlataformasDe(m)}
                />
              )))}
            </Box>
          </Box>
        ))
      )}

      {/* Nota en PDF y datos del requerimiento: de consulta, al final y compactos. */}
      <LoadingButton
        loading={printing}
        loadingPosition="start"
        startIcon={<PrintIcon />}
        variant="outlined"
        onClick={handleImprimir}
        sx={{ textTransform: "none", py: 1 }}
      >
        {printing ? "Generando…" : "Ver nota del mantenimiento (PDF)"}
      </LoadingButton>

      {req && <DatosRequerimientoPlegables req={req} />}

      <DetalleModemDialog
        modem={detalle}
        cod={cod}
        toAbsUrl={toAbsUrl}
        onClose={() => setDetalle(null)}
        onChanged={fetchAsignados}
        onToast={onToast}
        onVerEquipo={() => detalle && openCentroEquipo(detalle.num_sim)}
      />

      <PlataformasDialog
        modem={plataformasDe}
        reporte={plataformasDe ? reportes[claveReporte(plataformasDe)] : null}
        onReintentar={() => plataformasDe && verificarUno(plataformasDe)}
        onVerEquipo={() => plataformasDe && openCentroEquipo(plataformasDe.num_sim || plataformasDe.emai)}
        onClose={() => setPlataformasDe(null)}
      />

      <CentroEquipoDetalleDialog
        open={!!centroEquipo}
        tipo={centroEquipo?.tipo || "modem"}
        identificador={centroEquipo?.identificador}
        onClose={() => setCentroEquipo(null)}
      />
    </Stack>
  );
}

// ── Tarjetas ───────────────────────────────────────────────────

function CabeceraModem({ modem, atenuado = false, children }) {
  const chips = chipsDe(modem);
  const sub = [modem.modelo, chips.length ? `Chip ${chips.join(", ")}` : null].filter(Boolean).join(" · ")
    || (modem.emai ? modem.emai : "");
  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box
        sx={{
          width: 44, height: 44, borderRadius: "8px", display: "grid", placeItems: "center", flexShrink: 0,
          bgcolor: atenuado ? "action.hover" : "rgba(2,132,199,0.14)", color: atenuado ? "text.disabled" : AZUL_EQUIPOS,
        }}
      >
        <MemoryIcon />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: atenuado ? "text.secondary" : "text.primary" }} noWrap>
          {modem.num_sim || "—"}
        </Typography>
        {sub && <Typography variant="body2" color="text.secondary" noWrap>{sub}</Typography>}
      </Box>
      {children}
    </Stack>
  );
}

/** Equipo que queda en el vehículo: foto y si reporta, como lista de verificación. */
function ModemActivoCard({ modem, reporte, onVer, onVerEquipo, onVerReporte }) {
  const [anchor, setAnchor] = useState(null);
  const obs = observacionDe(modem);
  const esReemplazo = modem.estado_pendiente === "Remplazo Adicion";
  return (
    <Card variant="outlined" sx={{ borderRadius: "10px" }}>
      <CardActionArea onClick={onVer} component="div">
        <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
          <CabeceraModem modem={modem}>
            <IconButton
              size="small"
              aria-label="Opciones"
              onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <MoreVertIcon />
            </IconButton>
          </CabeceraModem>
          <Stack spacing={0.75} sx={{ mt: 1.25 }}>
            {esReemplazo && (
              <Typography variant="body2" color="text.secondary">
                Reemplaza al módem <b>{modem.modem_afectado || "—"}</b>.
              </Typography>
            )}
            <ItemVerificacion
              ok={tieneFoto(modem)}
              textoOk="Tiene foto del trabajo"
              textoFalta="Sin foto del trabajo"
              advertencia
            />
            <LineaReporte reporte={reporte} onClick={onVerReporte} />
            {obs && <Observacion texto={obs} />}
          </Stack>
        </CardContent>
      </CardActionArea>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => { setAnchor(null); onVer(); }}>
          <ListItemIcon><InfoOutlinedIcon fontSize="small" /></ListItemIcon>
          Ver detalles y foto
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); onVerEquipo(); }}>
          <ListItemIcon><MemoryIcon fontSize="small" /></ListItemIcon>
          Ver ficha del equipo
        </MenuItem>
      </Menu>
    </Card>
  );
}

/** Equipo reemplazado o devuelto: solo informativo y atenuado (ya no tiene que reportar). */
function ModemRetiradoCard({ modem, motivo, onVerEquipo }) {
  const obs = observacionDe(modem);
  return (
    <Card variant="outlined" sx={{ borderRadius: "10px", bgcolor: "action.hover", borderStyle: "dashed" }}>
      <CardActionArea onClick={onVerEquipo}>
        <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
          <CabeceraModem modem={modem} atenuado>
            <Box sx={{ px: 1.25, py: 0.25, borderRadius: 99, bgcolor: "action.selected", flexShrink: 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>{motivo}</Typography>
            </Box>
          </CabeceraModem>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {motivo === "Devuelto" ? "Se retiró del vehículo y vuelve a TRS." : "Se retiró del vehículo y se cambió por otro."}
          </Typography>
          {obs && <Box sx={{ mt: 1 }}><Observacion texto={obs} /></Box>}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// ── Detalle de un equipo ───────────────────────────────────────

function DetalleModemDialog({ modem, cod, toAbsUrl, onClose, onChanged, onToast, onVerEquipo }) {
  const obs = modem ? observacionDe(modem) : "";
  return (
    <Dialog open={!!modem} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "12px" } }}>
      {modem && (
        <>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: "8px", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0, background: "linear-gradient(135deg, #F59E0B, #C2410C)" }}>
              <BuildIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
                {modem.placa ? `Mantenimiento en ${modem.placa}` : "Mantenimiento"}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>{modem.num_sim || "—"}</Typography>
            </Box>
            <IconButton size="small" aria-label="Cerrar" onClick={onClose}><CloseIcon /></IconButton>
          </Stack>
          <DialogContent dividers sx={{ px: 2.5 }}>
            <Stack spacing={2.5}>
              <Seccion titulo="Equipo">
                <Grilla>
                  <Dato label="Placa" value={modem.placa} />
                  <Dato label="Modelo" value={modem.modelo} />
                  <Dato label="IMEI / EMAI" value={modem.emai} />
                  <Dato label={chipsDe(modem).length > 1 ? "Chips" : "Chip"} value={chipsDe(modem).join(", ")} />
                  <Dato label="Estado" value={modem.estado} />
                  {modem.modem_afectado && <Dato label="Reemplaza a" value={modem.modem_afectado} />}
                </Grilla>
                {obs && <Observacion texto={obs} />}
              </Seccion>
              <Seccion titulo="Foto del trabajo">
                <ModemImageManager
                  cod={cod}
                  numSim={modem.num_sim}
                  initialUrl={modem.imagen_url || ""}
                  toAbsUrl={toAbsUrl}
                  onChanged={onChanged}
                  onToast={onToast}
                />
              </Seccion>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 2.5, py: 1.5 }}>
            <Button onClick={onVerEquipo} sx={{ textTransform: "none", mr: "auto" }}>Ver ficha del equipo</Button>
            <Button onClick={onClose} variant="contained" sx={{ textTransform: "none", fontWeight: 700 }}>Cerrar</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
